import { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Listbox, Transition } from '@headlessui/react';
import {
  ChevronUpDownIcon,
  CheckIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { contentLibraryApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import clsx from 'clsx';
import { useTeamId } from '../hooks/useTeamId';

const STATUS_CONFIG: Record<string, { label: string; className: string; variant: BadgeVariant }> = {
  CURRENT: { label: 'No Issues', className: 'bg-green-100 text-green-700', variant: 'success' },
  EXPIRING: { label: 'Expiring', className: 'bg-yellow-100 text-yellow-700', variant: 'warning' },
  NEEDS_RE_REVIEW: { label: 'Needs Re-Review', className: 'bg-orange-100 text-orange-700', variant: 'warning' },
  UNDER_REVIEW: { label: 'Under Review', className: 'bg-blue-100 text-blue-700', variant: 'info' },
  NON_COMPLIANT: { label: 'Flagged for Review', className: 'bg-red-100 text-red-700', variant: 'danger' },
  ARCHIVED: { label: 'Archived', className: 'bg-gray-100 text-gray-500', variant: 'neutral' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'CURRENT', label: 'No Issues' },
  { value: 'EXPIRING', label: 'Expiring' },
  { value: 'NEEDS_RE_REVIEW', label: 'Needs Re-Review' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'NON_COMPLIANT', label: 'Flagged for Review' },
  { value: 'ARCHIVED', label: 'Archived' },
];

export default function ContentLibraryPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const limit = 25;

  // Team
  const { teamId } = useTeamId();

  // Content list
  const { data: contentData, isLoading } = useQuery({
    queryKey: ['contentLibrary', teamId, statusFilter, search, page],
    queryFn: () => contentLibraryApi.list(teamId!, {
      contentStatus: statusFilter || undefined,
      search: search || undefined,
      limit,
      offset: page * limit,
    }),
    enabled: !!teamId,
  });
  const reports = contentData?.data?.reports || [];
  const total = contentData?.data?.total || 0;

  // Stats
  const { data: statsData } = useQuery({
    queryKey: ['contentLibraryStats', teamId],
    queryFn: () => contentLibraryApi.stats(teamId!),
    enabled: !!teamId,
  });
  const stats = statsData?.data;

  // Mutations
  const archiveMutation = useMutation({
    mutationFn: (reportId: string) => contentLibraryApi.archive(teamId!, reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentLibrary'] });
      queryClient.invalidateQueries({ queryKey: ['contentLibraryStats'] });
      toast.success('Content archived');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to archive'),
  });

  const resubmitMutation = useMutation({
    mutationFn: (reportId: string) => contentLibraryApi.resubmit(teamId!, reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentLibrary'] });
      toast.success('Content marked for re-review');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to resubmit'),
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <FeatureGate feature="basic_compliance" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Content Library"
          description={`Track compliance lifecycle of all reviewed content (${total} items)`}
        />

        {/* Summary cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {Object.entries(STATUS_CONFIG).map(([key, { label, className }]) => (
              <div key={key} className="rounded-lg border bg-white p-3">
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-gray-900">{stats.byStatus?.[key] || 0}</p>
                <div className="mt-1">
                  <span className={clsx('inline-block h-1.5 rounded-full', className.replace(/text-\S+/, ''))}
                    style={{ width: `${stats.totalContent > 0 ? ((stats.byStatus?.[key] || 0) / stats.totalContent) * 100 : 0}%`, minWidth: '4px' }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pass rate + volume */}
        {stats && (
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <span>Pass Rate: <strong className="text-gray-900">{stats.passRate}%</strong></span>
            <span>Total Content: <strong className="text-gray-900">{stats.totalContent}</strong></span>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4">
          <Listbox value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <div className="relative w-48">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span>{STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {STATUS_FILTERS.map((f) => (
                    <Listbox.Option
                      key={f.value}
                      value={f.value}
                      className={({ active }) => clsx('relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')}
                    >
                      {({ selected }) => (
                        <>
                          <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search file name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Content table */}
        <div className="rounded-lg border bg-white">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : reports.length === 0 ? (
            <EmptyState
              icon={DocumentTextIcon}
              title="No content found"
              description="Your content library will populate as you run compliance checks."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">File Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Channel</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Reviewed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reports.map((r: any) => {
                    const effectiveStatus = r.effectiveContentStatus || r.contentStatus || 'CURRENT';
                    const st = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.CURRENT;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3">
                          <Link to={`/reports/${r.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                            {r.upload?.fileName || 'Unknown'}
                          </Link>
                        </td>
                        <td className="max-w-[200px] truncate px-4 py-3 text-sm text-gray-500">
                          {r.contentDescription || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {r.contentChannel || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {r.lastReviewedAt ? new Date(r.lastReviewedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {r.contentExpiresAt ? new Date(r.contentExpiresAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <StatusBadge variant={st.variant}>
                            {st.label}
                          </StatusBadge>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-2">
                            {effectiveStatus !== 'ARCHIVED' && (
                              <>
                                <button
                                  onClick={() => resubmitMutation.mutate(r.id)}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Re-submit for review"
                                >
                                  <ArrowPathIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm('Archive this content?')) archiveMutation.mutate(r.id);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Archive"
                                >
                                  <ArchiveBoxIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                  className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
